import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router';
import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import Loader from '@/components/atoms/Loader';
import { Routes } from '@/core/router/routes.types';
import { _routes as routes } from '@/core/router/routes';
import { getUserToken } from '@/redux/user';
import { useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useGetShiftQuery, usePutShiftMutation } from '@/services/innovorder/shift/shift.endpoints';
import { stringifyError } from '@/utils/errors';
import { ShiftForm, ShiftFormSubmitPayload } from '@/pages/Brand/ShiftForm';

const EditShift: React.FunctionComponent<React.PropsWithChildren> = () => {
    const history = useHistory();
    const { brandId, shiftId } = useParams<{ brandId: string; shiftId: string }>();
    const token = useSelector(getUserToken);

    const tabRoutes = useMemo(() => routes.filter(({ tab }) => tab === 'brands'), []);

    const { data: shift, isLoading: isLoadingShift } = useGetShiftQuery(
        { shiftId, token },
        { refetchOnMountOrArgChange: true },
    );
    const { data: restaurants = [] } = useGetBrandRestaurantsQuery({ brandId: parseInt(brandId, 10), token });

    const [putShift, { isSuccess, error, isLoading: isSaving }] = usePutShiftMutation();

    useEffect(() => {
        if (isSuccess) {
            history.push(generatePath(Routes.Shifts, { brandId }));
        }
    }, [isSuccess, history, brandId]);

    return (
        <RouterNavigationTabs routes={tabRoutes}>
            {isLoadingShift || !shift ? (
                <Loader withContainer />
            ) : (
                <ShiftForm
                    mode="edit"
                    shift={shift}
                    restaurants={restaurants}
                    isLoading={isSaving}
                    error={stringifyError(error)}
                    onSubmit={(payload: ShiftFormSubmitPayload) =>
                        putShift({
                            shiftId,
                            token,
                            name: payload.name,
                            daysOfWeek: payload.daysOfWeek,
                            start: payload.start,
                            end: payload.end,
                            endsTomorrow: payload.endsTomorrow,
                            color: payload.color,
                            isEnabled: payload.isEnabled,
                        })
                    }
                />
            )}
        </RouterNavigationTabs>
    );
};

export default EditShift;
